// Google Maps API Service for location and distance calculations
// This service uses Google Maps JavaScript SDK (not direct API calls)

class GoogleMapsService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    this.isLoaded = false;
    this.loadPromise = null;
    this.placesService = null;
    this.geocoder = null;
    this.distanceService = null;
  }

  // Check if API is configured
  isConfigured() {
    const hasValidKey = !!(this.apiKey && 
      this.apiKey !== 'YOUR_ACTUAL_API_KEY_HERE' && 
      this.apiKey !== 'your_actual_api_key_here' &&
      this.apiKey.length > 10);
    
    console.log('[GoogleMapsService] API Key Status:', {
      hasKey: !!this.apiKey,
      keyLength: this.apiKey ? this.apiKey.length : 0,
      isValid: hasValidKey
    });
    
    return hasValidKey;
  }

  // Load Google Maps JavaScript API
  async loadGoogleMapsAPI() {
    if (this.isLoaded) {
      console.log('[GoogleMapsService] API already loaded');
      return Promise.resolve();
    }

    if (this.loadPromise) {
      console.log('[GoogleMapsService] API loading in progress...');
      return this.loadPromise;
    }

    if (!this.isConfigured()) {
      console.error('[GoogleMapsService] API key not configured');
      throw new Error('Google Maps API key not configured');
    }

    console.log('[GoogleMapsService] Starting to load Google Maps API...');

    this.loadPromise = new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        console.log('[GoogleMapsService] Google Maps API already available in window');
        this.isLoaded = true;
        this.initializeServices();
        resolve();
        return;
      }

      const script = document.createElement('script');
      const apiUrl = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`;
      script.src = apiUrl;
      script.async = true;
      script.defer = true;

      console.log(`[GoogleMapsService] Loading script: ${apiUrl}`);

      script.onload = () => {
        console.log('[GoogleMapsService] Script loaded successfully');
        this.isLoaded = true;
        this.initializeServices();
        resolve();
      };

      script.onerror = (error) => {
        console.error('[GoogleMapsService] Failed to load Google Maps API script:', error);
        console.error('[GoogleMapsService] Script URL:', apiUrl);
        console.error('[GoogleMapsService] Possible issues:');
        console.error('  - API key invalid or expired');
        console.error('  - Domain not authorized in Google Cloud Console');
        console.error('  - HTTPS required for production');
        console.error('  - API quota exceeded');
        reject(new Error('Failed to load Google Maps API - Check console for details'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  // Initialize Google Maps services
  initializeServices() {
    if (window.google && window.google.maps) {
      // Create a dummy div for PlacesService (required)
      const dummyDiv = document.createElement('div');
      this.placesService = new window.google.maps.places.PlacesService(dummyDiv);
      this.geocoder = new window.google.maps.Geocoder();
      this.distanceService = new window.google.maps.DistanceMatrixService();
    }
  }

  // Search locations using Google Places API
  async searchLocations(query) {
    try {
      if (!this.apiKey) {
        throw new Error('Google Maps API key not configured');
      }

      // Use Places API Text Search
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/place/textsearch/json?query=${encodeURIComponent(query)}&key=${this.apiKey}&region=in&location=13.0827,80.2707&radius=500000`
      );

      if (!response.ok) {
        throw new Error(`Google Places API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && data.results) {
        return data.results.slice(0, 5).map(place => ({
          id: place.place_id,
          name: place.name,
          address: place.formatted_address,
          coords: {
            lat: place.geometry.location.lat,
            lng: place.geometry.location.lng
          },
          types: place.types,
          rating: place.rating
        }));
      }

      return [];
    } catch (error) {
      console.error('Google Places API error:', error);
      throw error;
    }
  }

  // Get location suggestions using Google Places Autocomplete API
  async getLocationSuggestions(query) {
    try {
      if (!this.isConfigured()) {
        console.error('[GoogleMapsService] API key not configured');
        throw new Error('Google Maps API key not configured');
      }

      console.log(`[GoogleMapsService] Searching for: ${query}`);
      console.log(`[GoogleMapsService] API Key configured: ${this.apiKey ? 'Yes' : 'No'}`);

      // Load Google Maps API first
      await this.loadGoogleMapsAPI();
      console.log('[GoogleMapsService] Google Maps API loaded successfully');

      // Use Google Maps JavaScript API AutocompleteService
      return new Promise((resolve, reject) => {
        try {
          const service = new window.google.maps.places.AutocompleteService();
          console.log('[GoogleMapsService] AutocompleteService created');

          service.getPlacePredictions({
            input: query,
            componentRestrictions: { country: 'in' },
            types: ['geocode', 'establishment'],
            location: new window.google.maps.LatLng(13.0827, 80.2707),
            radius: 500000
          }, (predictions, status) => {
            console.log(`[GoogleMapsService] API Response - Status: ${status}, Predictions:`, predictions);

            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              const results = predictions.slice(0, 5).map(prediction => ({
                id: prediction.place_id,
                name: prediction.structured_formatting?.main_text || prediction.description,
                address: prediction.description,
                coords: null, // Will be geocoded when needed
                types: prediction.types || [],
                rating: null
              }));

              console.log(`[GoogleMapsService] Successfully fetched ${results.length} suggestions for: ${query}`);
              resolve(results);
            } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              console.log('[GoogleMapsService] No results found');
              resolve([]);
            } else {
              console.error(`[GoogleMapsService] API Error: ${status}`);
              reject(new Error(`Google Places API error: ${status}`));
            }
          });
        } catch (serviceError) {
          console.error('[GoogleMapsService] Error creating AutocompleteService:', serviceError);
          reject(serviceError);
        }
      });
    } catch (error) {
      console.error('Google Places Autocomplete API error:', error);
      throw error;
    }
  }

  // Get detailed information about a place using Place Details API
  async getPlaceDetails(placeId) {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${this.apiKey}&fields=geometry,formatted_address,name,types`
      );

      if (!response.ok) {
        throw new Error(`Google Place Details API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && data.result) {
        return {
          coords: {
            lat: data.result.geometry.location.lat,
            lng: data.result.geometry.location.lng
          },
          address: data.result.formatted_address,
          name: data.result.name,
          types: data.result.types
        };
      }

      throw new Error('Place details not found');
    } catch (error) {
      console.error('Google Place Details API error:', error);
      throw error;
    }
  }

  // Calculate distance using Google Distance Matrix API - Optimized for car/taxi routes
  async calculateDistance(origin, destination) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Google Maps API key not configured');
      }

      // Load Google Maps API first
      await this.loadGoogleMapsAPI();

      // Use Google Maps JavaScript API DistanceMatrixService
      return new Promise((resolve, reject) => {
        const service = new window.google.maps.DistanceMatrixService();

        console.log(`[GoogleMapsService] Calculating car route distance from:`, origin, 'to:', destination);

        service.getDistanceMatrix({
          origins: [new window.google.maps.LatLng(origin.lat, origin.lng)],
          destinations: [new window.google.maps.LatLng(destination.lat, destination.lng)],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
          avoidHighways: false,
          avoidTolls: false,
          avoidFerries: true,
          region: 'IN',
          language: 'en',
          drivingOptions: {
            departureTime: new Date(),
            trafficModel: 'bestguess'
          }
        }, (response, status) => {
          console.log(`[GoogleMapsService] Car Route Distance Matrix Response - Status: ${status}`, response);

          if (status === window.google.maps.DistanceMatrixStatus.OK) {
            const element = response.rows[0].elements[0];

            if (element.status === 'OK') {
              const distanceInKm = Math.round((element.distance.value / 1000) * 100) / 100;
              const durationInMinutes = Math.round(element.duration.value / 60);

              const result = {
                distance: distanceInKm,
                duration: element.duration.text,
                distanceText: element.distance.text,
                durationValue: element.duration.value,
                durationMinutes: durationInMinutes,
                routeType: 'DRIVING'
              };

              console.log(`[GoogleMapsService] Car route calculated: ${result.distanceText}, ${result.duration} (${durationInMinutes} minutes)`);

              resolve(result);
            } else {
              console.error(`[GoogleMapsService] Car route calculation failed: ${element.status}`);
              reject(new Error(`Car route calculation failed: ${element.status}`));
            }
          } else {
            console.error(`[GoogleMapsService] Distance Matrix API error: ${status}`);
            reject(new Error(`Google Distance Matrix API error: ${status}`));
          }
        });
      });
    } catch (error) {
      console.error('Google Distance Matrix API error:', error);
      throw error;
    }
  }

  // Geocode an address to get coordinates
  async geocodeAddress(address) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Google Maps API key not configured');
      }

      // Load Google Maps API first
      await this.loadGoogleMapsAPI();

      // Use Google Maps JavaScript API Geocoder
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({
          address: address,
          region: 'in'
        }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
            const result = results[0];
            resolve({
              coords: {
                lat: result.geometry.location.lat(),
                lng: result.geometry.location.lng()
              },
              address: result.formatted_address,
              types: result.types
            });
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });
    } catch (error) {
      console.error('Google Geocoding API error:', error);
      throw error;
    }
  }

  // Reverse geocode coordinates to get address
  async reverseGeocode(lat, lng) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Google Maps API key not configured');
      }

      // Load Google Maps API first
      await this.loadGoogleMapsAPI();

      // Use Google Maps JavaScript API Geocoder
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({
          location: { lat: lat, lng: lng }
        }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
            resolve({
              address: results[0].formatted_address,
              components: results[0].address_components
            });
          } else {
            reject(new Error(`Reverse geocoding failed: ${status}`));
          }
        });
      });
    } catch (error) {
      console.error('Google Reverse Geocoding API error:', error);
      throw error;
    }
  }
}

export default new GoogleMapsService();
